import React, { useState } from 'react';
import { Card, Badge, Button, Row, Col, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faCalendarAlt,
    faClock,
    faFlag,
    faUser,
    faTags,
    faEllipsisV,
    faCheckCircle,
    faPlayCircle,
    faPauseCircle,
    faStopCircle,
    faLink
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { supabaseDataService } from '../services/supabaseService';
import DeleteConfirmationModal from "@/components/ConfirmationModal.jsx";


const TaskItem = ({
                      task,
                      onEdit,
                      onUpdate,
                      onDelete,
                      getPriorityBadgeVariant,
                      getStatusBadgeVariant
                  }) => {
    const [loading, setLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };
    const handleDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        onDelete(task);
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };


    const handleStatusChange = async (newStatus) => {
        try {
            setLoading(true);
            const updatedTask = { ...task, status: newStatus };

            // Update in database
            await supabaseDataService.updateTask(task.task_id, { status: newStatus });

            // Update local state
            onUpdate(updatedTask);
        } catch (error) {
            console.error('Error updating task status:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setShowDeleteConfirm(true);
    };

    const formatDate = (date) => {
        if (!date) return 'No due date';
        return moment(date).format('MMM DD, YYYY');
    };

    const formatTime = (minutes) => {
        if (!minutes) return 'Not estimated';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
        }
        return `${mins}m`;
    };

    const isOverdue = () => {
        if (!task.due_date) return false;
        return moment(task.due_date).isBefore(moment(), 'day') && task.status !== 'completed';
    };

    const getDaysUntilDue = () => {
        if (!task.due_date) return null;
        const daysLeft = moment(task.due_date).diff(moment(), 'days');
        return daysLeft;
    };

    const renderDueDateBadge = () => {
        const daysLeft = getDaysUntilDue();

        if (isOverdue()) {
            return <Badge bg="danger" className="ms-2">Overdue</Badge>;
        }

        if (daysLeft === 0) {
            return <Badge bg="warning" className="ms-2">Due Today</Badge>;
        }

        if (daysLeft === 1) {
            return <Badge bg="warning" className="ms-2">Due Tomorrow</Badge>;
        }

        if (daysLeft <= 3 && daysLeft > 0) {
            return <Badge bg="info" className="ms-2">Due in {daysLeft} days</Badge>;
        }

        return null;
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return faCheckCircle;
            case 'in progress':
                return faPlayCircle;
            case 'pending':
                return faPauseCircle;
            case 'blocked':
                return faStopCircle;
            default:
                return faPlayCircle;
        }
    };

    return (
        <>
        <Card className={`mb-3 task-item ${isOverdue() ? 'border-danger' : ''}`}>
            <Card.Body>
                <Row className="align-items-start">
                    <Col xs={12} md={8}>
                        <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon
                                icon={getStatusIcon(task.status)}
                                className={`me-2 text-${getStatusBadgeVariant(task.status)}`}
                            />
                            <h6 className="mb-0 me-2">{task.title || 'Untitled Task'}</h6>
                            <Badge bg={getPriorityBadgeVariant(task.priority)} className="me-2">
                                <FontAwesomeIcon icon={faFlag} className="me-1" />
                                {task.priority || 'Normal'}
                            </Badge>
                            <Badge bg={getStatusBadgeVariant(task.status)}>
                                {task.status || 'Pending'}
                            </Badge>
                            {renderDueDateBadge()}
                        </div>

                        {task.description && (
                            <p className="text-muted mb-2 small">
                                {task.description.length > 150
                                    ? `${task.description.substring(0, 150)}...`
                                    : task.description
                                }
                            </p>
                        )}

                        <div className="d-flex flex-wrap gap-2 align-items-center small text-muted">
                            {task.category && (
                                <span>
                                    <FontAwesomeIcon icon={faTags} className="me-1" />
                                    {task.category}
                                </span>
                            )}

                            {task.assigned_to && (
                                <span>
                                    <FontAwesomeIcon icon={faUser} className="me-1" />
                                    {task.assigned_to}
                                </span>
                            )}

                            {task.estimated_time && (
                                <span>
                                    <FontAwesomeIcon icon={faClock} className="me-1" />
                                    {formatTime(task.estimated_time)}
                                </span>
                            )}

                            <span>
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                Created {moment(task.created_at).format('MMM DD, YYYY')}
                            </span>
                        </div>

                        {task.tags && task.tags.length > 0 && (
                            <div className="mt-2">
                                {task.tags.map((tag, index) => (
                                    <Badge
                                        key={index}
                                        bg="light"
                                        text="dark"
                                        className="me-1 small"
                                    >
                                        {tag}
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </Col>

                    <Col xs={12} md={4} className="text-end">
                        <div className="mb-2">
                            <small className="text-muted">
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                Due: {formatDate(task.due_date)}
                            </small>
                        </div>

                        <div className="d-flex justify-content-end gap-1">
                            {/* Quick Status Actions */}
                            {task.status !== 'completed' && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Mark as Complete</Tooltip>}
                                >
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={() => handleStatusChange('completed')}
                                        disabled={loading}
                                    >
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                    </Button>
                                </OverlayTrigger>
                            )}

                            {task.status !== 'in progress' && task.status !== 'completed' && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Start Task</Tooltip>}
                                >
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleStatusChange('in progress')}
                                        disabled={loading}
                                    >
                                        <FontAwesomeIcon icon={faPlayCircle} />
                                    </Button>
                                </OverlayTrigger>
                            )}

                            {/*<OverlayTrigger*/}
                            {/*    placement="top"*/}
                            {/*    overlay={<Tooltip>Edit Task</Tooltip>}*/}
                            {/*>*/}
                            {/*    <Button*/}
                            {/*        variant="outline-secondary"*/}
                            {/*        size="sm"*/}
                            {/*        onClick={() => onEdit(task)}*/}
                            {/*        disabled={loading}*/}
                            {/*    >*/}
                            {/*        <FontAwesomeIcon icon={faEdit} />*/}
                            {/*    </Button>*/}
                            {/*</OverlayTrigger>*/}

                            {true && (
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={loading}
                                >
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => onEdit(task)}>
                                        <FontAwesomeIcon icon={faEye} className="me-2" />
                                        View Details
                                    </Dropdown.Item>

                                    <Dropdown.Divider />

                                    <Dropdown.Item
                                        onClick={() => handleStatusChange('pending')}
                                        disabled={task.status === 'pending'}
                                    >
                                        <FontAwesomeIcon icon={faPauseCircle} className="me-2" />
                                        Set to Pending
                                    </Dropdown.Item>

                                    <Dropdown.Item
                                        onClick={() => handleStatusChange('in progress')}
                                        disabled={task.status === 'in progress'}
                                    >
                                        <FontAwesomeIcon icon={faPlayCircle} className="me-2" />
                                        Set to In Progress
                                    </Dropdown.Item>

                                    {/*<Dropdown.Item*/}
                                    {/*    onClick={() => handleStatusChange('blocked')}*/}
                                    {/*    disabled={task.status === 'blocked'}*/}
                                    {/*>*/}
                                    {/*    <FontAwesomeIcon icon={faStopCircle} className="me-2" />*/}
                                    {/*    Mark as Blocked*/}
                                    {/*</Dropdown.Item>*/}

                                    <Dropdown.Item
                                        onClick={() => handleStatusChange('completed')}
                                        disabled={task.status === 'completed'}
                                    >
                                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                        Mark as Complete
                                    </Dropdown.Item>

                                    <Dropdown.Divider />

                                    <Dropdown.Item
                                        onClick={handleDelete}
                                        className="text-danger"
                                    >
                                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                                        Delete Task
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* Dependencies */}
                {task.dependencies && (
                    <Row className="mt-2">
                        <Col>
                            <small className="text-muted">
                                <FontAwesomeIcon icon={faLink} className="me-1" />
                                Dependencies: {task.dependencies}
                            </small>
                        </Col>
                    </Row>
                )}
            </Card.Body>
        </Card>
        <DeleteConfirmationModal
            show={showDeleteConfirm}
            onHide={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title={task?.title || 'Untitled '}
            subtitle={task?.description || 'Untitled'}
            message="Are you sure you want to delete this task?"
            confirmButtonText="Delete Task"
        />
    </>
    );
};

export default TaskItem;